import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Tooltip,
} from "@mui/material";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import DataTable from "../Tables/DataTable/DataTable";
import "./Reports.css";
import moment from "moment";
import {
  REPORT,
  REPORT_EXPORT,
} from "../../constants/api-constants";
import ApiServices from "../../services/ApiServices";
import { toast } from "react-toastify";
import { useLoading } from "../../hooks/useLoader";
import ExportToExcelHandler from "../../utilities/ExportToExcelHandler";
import { COLUMN_TYPES } from "../../constants/common";

/*istanbul ignore next */
const Reports = ({
  selectedReport = "",
  dateFrom = null,
  dateTo = null,
  searchToken = 0,
  onReportLoadingChange = () => {},
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [adminTable, setAdminTableData] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [exportDateFrom, setExportDateFrom] = useState(null);
  const [exportDateTo, setExportDateTo] = useState(null);
  const { showLoader } = useLoading();
  const [previousPageToken, setPreviousPageToken] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setSelectedColumns([]);
    setAdminTableData([]);
    setNextPageToken(null);
    setPreviousPageToken(null);
    setCurrentPage(1);
  }, [selectedReport]);

  useEffect(() => {
    if (searchToken > 0 && selectedReport) {
      reportDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchToken]);

  const handleNextPage = () => {
    handleChangePageNumber(nextPageToken);
    setCurrentPage((prevPage) => prevPage + 1);
  };
  const handlePreviousPage = () => {
    handleChangePageNumber(previousPageToken);
    setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : 1));
  };

  const pageSizeChangeHandler = (newPageSize) => {
    const parsedPageSize = Number.parseInt(newPageSize);
    setPageSize(parsedPageSize);
    handleChangePageNumber(null, newPageSize, true);
  };

  const mapFieldType = (field) => {
    if (field.toLowerCase().includes("date")) return COLUMN_TYPES.DATE;
    if (
      field.toLowerCase().includes("is") ||
      field.toLowerCase().includes("status")
    )
      return COLUMN_TYPES.BASIC;
    return COLUMN_TYPES.BASIC;
  };

  const flattenFields = (obj, parentKey = "") => {
    if (!obj || typeof obj !== "object") {
      return [];
    }
    const fields = [];
    Object.keys(obj).forEach((key) => {
      const fieldKey = parentKey ? `${parentKey}.${key}` : key;
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        fields.push(...flattenFields(obj[key], fieldKey));
      } else {
        const label = key
          .replaceAll(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        fields.push({
          fieldName: fieldKey,
          label: label,
          exportLabel: "",
          sort: false,
          isSelected: true,
          type: mapFieldType(fieldKey),
          width: 250,
          showInSelection: false,
        });
      }
    });
    return fields;
  };

  const formatDate = (key, value) => {
    if (key === "loginTime" || key === "createdOn") {
      return moment(value).format("MM/DD/YYYY hh:mm A");
    }
    return value;
  };

  const processNestedObject = (flattenedRecord, key, nestedObj) => {
    Object.keys(nestedObj).forEach((nestedKey) => {
      flattenedRecord[`${key}.${nestedKey}`] = nestedObj[nestedKey];
    });
  };

  const flattenRecord = (record) => {
    if (!record || typeof record !== "object") {
      return {};
    }
    const flattenedRecord = {};
    Object.keys(record).forEach((key) => {
      const value = record[key];
      const isObject = typeof value === "object" && value !== null;
      if (isObject) {
        processNestedObject(flattenedRecord, key, value);
      } else {
        flattenedRecord[key] = formatDate(key, value);
      }
    });
    return flattenedRecord;
  };

  const flattenDataRows = (data) => {
    return data.map(flattenRecord);
  };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleChangePageNumber = async (
    pageToken = "",
    updatedPageSize = pageSize,
    resetPageTokens = false
  ) => {
    try {
      const payload = {
        pageSize: Number.parseInt(updatedPageSize),
        pageNumber: 1,
        previousPageToken: resetPageTokens ? null : previousPageToken,
        nextPageToken: resetPageTokens ? null : nextPageToken,
        ignorePaging: false,
        reportName: selectedReport,
        filter: {
          timeZone: userTimeZone,
          keyword: "",
          dateFrom: dateFrom
            ? moment(dateFrom).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
          dateTo: dateTo
            ? moment(dateTo).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
        },
      };
      const response = await ApiServices.httpPost(REPORT, payload);
      if (response.responseStatus.isSuccess) {
        const records = Array.isArray(response?.result?.records)
          ? response.result.records
          : [];
        const flattenedRecords = records.length
          ? flattenDataRows(records)
          : [];
        setAdminTableData(flattenedRecords);
        setNextPageToken(response?.result?.nextPageToken);
        setPreviousPageToken(response?.result?.previousPageToken);
      } else {
        toast.error(response.responseStatus.error);
      }
    } catch (e) {
      toast.error("Internal server error");
    }
  };

  const reportDetails = async () => {
    onReportLoadingChange(true);
    try {
      const payload = {
        pageSize: 10,
        pageNumber: 1,
        ignorePaging: false,
        reportName: selectedReport,
        filter: {
          timeZone: userTimeZone,
          keyword: "",
          dateFrom: dateFrom
            ? moment(dateFrom).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
          dateTo: dateTo
            ? moment(dateTo).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
        },
      };
      const response = await ApiServices.httpPost(REPORT, payload);
      
      if (response.responseStatus.isSuccess) {
        const records = Array.isArray(response?.result?.records)
          ? response.result.records
          : [];
          
        setExportDateFrom(dateFrom);
        setExportDateTo(dateTo);
        if (records.length > 0) {
          const columns = flattenFields(records[0]);
          setSelectedColumns(columns);
          const flattenedRecords = flattenDataRows(records);
          setAdminTableData(flattenedRecords);
          setNextPageToken(response?.result?.nextPageToken || null);
          setPreviousPageToken(response?.result?.previousPageToken || null);
          toast.success("Report Generated Successfully");
        } else {
          toast.error(
            "No records found. Please refine your search criteria and try again."
          );
          setAdminTableData([]);
        }
      } else {
        toast.error(response.responseStatus.error);
      }
    } catch (e) {
      toast.error("Internal server error");
    } finally {
      onReportLoadingChange(false);
    }
  };

  const handleExport = async () => {
    try {
      let payload = {
        pageSize: 10,
        pageNumber: 1,
        nextPageToken: nextPageToken,
        previousPageToken: previousPageToken,
        ignorePaging: false,
        reportName: selectedReport,
        filter: {
          timeZone: userTimeZone,
          keyword: "",
          dateFrom: exportDateFrom
            ? moment(exportDateFrom).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
          dateTo: exportDateTo
            ? moment(exportDateTo).format("YYYY-MM-DDT00:00:00.000[Z]")
            : "",
        },
      };
      showLoader(true);
      let exportedContent;
      exportedContent = await ApiServices.httpPost(REPORT_EXPORT, payload);
      if (exportedContent?.result?.file) {
        ExportToExcelHandler(
          exportedContent.result.file.fileContents,
          exportedContent.result.file.fileDownloadName
        );
      }
      showLoader(false);
    } catch (e) {
      showLoader(false);
      toast.error("Internal server error");
    }
  };

  return (
    <Grid spacing={2} container className="reports-container">
      <Grid item xs={12} justifyContent={"flex-end"} display={"flex"} mr={2}>
        <div className="add-welcome-widgets">
          <Tooltip
            placement="bottom"
            title={
              adminTable.length > 0 ? "Export to Excel" : "No data to export"
            }
          >
            <IosShareRoundedIcon
              data-testid="IosShareRoundedIcon"
              onClick={adminTable.length > 0 ? handleExport : null}
              style={{
                cursor: adminTable.length > 0 ? "pointer" : "not-allowed",
                background: adminTable.length > 0 ? "#1aaefa" : "lightgray",
              }}
            />
          </Tooltip>
        </div>
      </Grid>
      <Grid item xs={11.8} ml={2}>
        {adminTable.length > 0 && (
          <DataTable
            uniqueKey="userReports"
            adminTable={adminTable}
            columns={selectedColumns}
            serachBarNotRequired={true}
            checkboxesNotRequired={true}
            customPagination={true}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            hasNextPage={nextPageToken}
            hasPreviousPage={previousPageToken}
            onPageSizeChange={pageSizeChangeHandler}
            currentPage={currentPage}
          />
        )}
      </Grid>
    </Grid>
  );
};

Reports.propTypes = {
  selectedReport: PropTypes.string,
  dateFrom: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  dateTo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  searchToken: PropTypes.number,
  onReportLoadingChange: PropTypes.func,
};

export default Reports;
